/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoSwmInvProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoSwmInvProps {
    /**
     * A string representing the CSS class to be applied to the LogoSwmInvIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LogoSwmInvIcon element.
     *
     * @default 28
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoSwmInv'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoSwmInvIcon element.
     *
     * @default 83
     */
    width?: number | string;
}

/**
 * The `LogoSwmInv` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoSwmInvProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoSwmInvIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoSwmInvIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoSwmInvIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoSwmInvComponent = <LogoSwmInv className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoSwmInv = ({
    className = '',
    color1 = '#fff',
    height = 32,
    ref,
    testId = 'LogoSwmInv',
    width = 93
}: LogoSwmInvProps) => (
    <svg
        ref={ref}
        aria-label="SWM Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 93 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M67.22.83 50.49 31.17h5.95L73.14.83zm-9.77 0L40.73 31.17h5.94L63.38.83zM21.38 5.47V26.5h5.44l7-12.13V26.5h5.62L51.09 5.47h-5.2l-6.53 11.41V5.48h-5.93l-6.61 11.17V5.47zM62.9 26.5h5.26l6.46-11.25V26.5h5.67l6.88-11.22V26.5h5.5V5.47h-5.5L80.29 17.6V5.47h-5.8zM5.87 19.3a3.67 3.67 0 0 0 3.69 3.58c2.03 0 3.68-1.01 3.68-2.27 0-.73-.41-1.6-2.08-2.03l-2.02-.6c-1.13-.34-1.8-.43-2.91-.82-.74-.26-1.14-.45-1.87-.73-2.27-1.2-3.53-3.17-3.4-5.4C.78 7.37 4.86 4.6 9.64 4.6c4.79 0 8.67 2.88 8.67 6.44v.59h-5.65l.01-.4c0-1.2-1.35-2.15-3.03-2.15l-.37.02c-1.46 0-2.66.66-2.66 1.48v.23c0 .6.52 1.14 1.31 1.46.9.36 1.36.47 2.28.73 1.03.3 1.62.43 2.64.77a54 54 0 0 1 2.73 1.05c1.85.8 3.33 2.7 3.33 4.5.26 4.77-3.05 7.68-9.32 7.68-6.24 0-9.32-2.84-9.32-7.69z" fill={color1} />
    </svg>
);

LogoSwmInv.displayName = 'LogoSwmInv';